import {
  ARROWS,
  COUNT_IN_ROW,
  YEARS_LIST_INTERVAL,
  CALENDAR_NAVIGATION_ID,
} from './constants';
import generateId from '../utils/generateId';

export const formatMinimumIntegerDigits = (
  minimumIntegerDigits: number,
  value: number
) => new Intl.NumberFormat('ru-RU', { minimumIntegerDigits }).format(value);

export const parseDate = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const formattedMonth = formatMinimumIntegerDigits(2, month);
  const formattedDay = formatMinimumIntegerDigits(2, day);

  return `${year}-${formattedMonth}-${formattedDay}`;
};

export const getStartYearsInterval = (date: Date) => {
  const year = date.getFullYear();
  return Math.floor(year / YEARS_LIST_INTERVAL) * YEARS_LIST_INTERVAL;
};

export type CalendarNavigationType = 'days' | 'months' | 'years';
export const getOffset = (
  type: CalendarNavigationType,
  arrowType: keyof typeof ARROWS
) => {
  const offset = {
    [ARROWS.ArrowLeft]: -1,
    [ARROWS.ArrowRight]: 1,
    [ARROWS.ArrowUp]: -COUNT_IN_ROW[type],
    [ARROWS.ArrowDown]: COUNT_IN_ROW[type],
  };
  return offset[arrowType];
};

// eslint-disable-next-line @typescript-eslint/ban-types
type StyleType = object;
type AdditionalStyleType = [boolean, StyleType];

export const combineStyles = (
  baseStyle: StyleType,
  ...additionalStyles: AdditionalStyleType[]
): StyleType => {
  let styles = { ...baseStyle };
  additionalStyles.forEach(([condition, style]) => {
    if (condition) {
      styles = { ...styles, ...style };
    }
  });
  return styles;
};

export const isDateInInterval = (
  startDate: string,
  endDate: string,
  date: string
) => {
  const startTime = new Date(startDate).getTime();
  const endTime = new Date(endDate).getTime();
  const currentTime = new Date(date).getTime();
  return currentTime > startTime && currentTime < endTime;
};

export const getMonthDaysCount = (date: Date) => {
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();
  return new Date(currentYear, currentMonth + 1, 0).getDate();
};

export const isDateValid = (date: string) => parseDate(new Date(date)) === date;

export const getStructuredDisabledMonths = (disabledDates: string[]) => {
  const uniqDisabledDates = Array.from(new Set(disabledDates));
  const validDisabledDates = uniqDisabledDates.filter((date) =>
    isDateValid(date)
  );
  // Пример structuredDisabledDaysInMonths: { '2021': { '09': 30, '10': 11 }, '2022': { '01': 31 } }
  const structuredDisabledDays = validDisabledDates.reduce(
    (result, currentDate) => {
      const [year, month] = currentDate.split('-');
      if (year in result) {
        if (month in result[year]) {
          result[year][month] += 1;
        } else {
          result[year][month] = 1;
        }
      } else {
        result[year] = { [month]: 1 };
      }
      return result;
    },
    {} as Record<string, Record<string, number>>
  );
  // Приводим к формату { '2021': [9], '2022': [1] }
  const structuredDisabledMonths = Object.entries(
    structuredDisabledDays
  ).reduce<Record<string, number[]>>((result, [year, value]) => {
    result[year] = [];
    Object.entries(value).forEach(([month, daysCount]) => {
      const isFullMonthDisabled =
        getMonthDaysCount(new Date(+year, +month, 0)) === daysCount;
      if (isFullMonthDisabled) {
        result[year].push(+month);
      }
    });
    return result;
  }, {});
  return structuredDisabledMonths;
};

export function isArrow(key: string): key is keyof typeof ARROWS {
  return key in ARROWS;
}

export function generateCalendarId(baseId: string = generateId()) {
  return `${baseId}-${CALENDAR_NAVIGATION_ID}`;
}

export function getDateParts(date: string) {
  const formattedDate = new Date(date);
  return {
    year: formattedDate.getFullYear(),
    month: formattedDate.getMonth(),
    day: formattedDate.getDate(),
  };
}

export function getFirstSelectedDate(
  date: string,
  selectedDates: string[],
  type: 'year' | 'month'
) {
  const { month: dateMonth, year: dateYear } = getDateParts(date);
  const firstSelectedDate = selectedDates.sort().find((selectedDate) => {
    const { month: selectedDateMonth, year: selectedDateYear } = getDateParts(
      selectedDate
    );
    const isYearEqual = dateYear === selectedDateYear;
    return type === 'year'
      ? isYearEqual
      : isYearEqual && dateMonth === selectedDateMonth;
  })!;
  return firstSelectedDate;
}
